import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import { BOUTIQUES, PRODUITS, type Produit } from '../data/mock-data';
import { fraisLivraison, prixPour, poidsParDefaut } from '../data/repository';

/* Index construit une fois : un panier de trente lignes ne relit pas le catalogue. */
const INDEX = new Map(PRODUITS.map(p => [p.id, p]));

/**
 * ÉTAT DE LA BOUTIQUE — un seul endroit, quatre préoccupations.
 *
 *   1. le panier (lignes, poids, options, quantité) ;
 *   2. le mode de service (retrait / livraison) et la boutique choisie ;
 *   3. les favoris et le confort de lecture ;
 *   4. le tiroir de panier et les messages brefs (toasts).
 *
 * Le panier est **persisté** : fermer son navigateur au milieu d'une commande ne
 * fait rien perdre. Rien n'est envoyé avant la page de commande — les prix
 * affichés restent des estimations que le serveur recalculera.
 */

export interface LignePanier {
  cle: string;
  produitId: string;
  poidsG: number;
  quantite: number;
  formatIndex?: number;
  options: Record<string, string>;
  /** Mot libre laissé au boucher (200 caractères) : imprimé sur le ticket de découpe. */
  note?: string;
}

export interface LigneDetaillee extends LignePanier {
  produit: Produit;
  prixUnitaire: number;
  total: number;
}

interface Toast {
  id: number;
  message: string;
  libelleAction?: string;
  action?: () => void;
}

interface API {
  lignes: LigneDetaillee[];
  nbArticles: number;
  sousTotal: number;
  total: number;
  frais: number;
  mode: 'PICKUP' | 'DELIVERY';
  boutiqueId: string;
  panierOuvert: boolean;
  favoris: string[];
  confort: boolean;
  toast: Toast | null;
  ajouter: (p: Produit, opts?: { poidsG?: number; quantite?: number; formatIndex?: number; options?: Record<string, string>; note?: string }) => void;
  majQuantite: (cle: string, quantite: number) => void;
  majPoids: (cle: string, poidsG: number) => void;
  retirer: (cle: string) => void;
  vider: () => void;
  setMode: (m: 'PICKUP' | 'DELIVERY') => void;
  setBoutique: (id: string) => void;
  ouvrirPanier: (ouvert: boolean) => void;
  basculerFavori: (id: string) => void;
  basculerConfort: () => void;
  annoncer: (message: string, action?: { libelle: string; faire: () => void }) => void;
  fermerToast: () => void;
  indisponibles: Produit[];
}

const Ctx = createContext<API | null>(null);
const CLE = 'md.vitrine.v1';

interface Persiste {
  lignes: LignePanier[];
  mode: 'PICKUP' | 'DELIVERY';
  boutiqueId: string;
  favoris: string[];
  confort: boolean;
}

const initial: Persiste = {
  lignes: [],
  mode: 'PICKUP',
  boutiqueId: BOUTIQUES[0].id,
  favoris: [],
  confort: false,
};

function lire(): Persiste {
  if (typeof window === 'undefined') return initial;
  try {
    const brut = window.localStorage.getItem(CLE);
    if (!brut) return initial;
    const lu = JSON.parse(brut) as Partial<Persiste>;
    return {
      lignes: Array.isArray(lu.lignes) ? lu.lignes.filter(l => INDEX.has(l.produitId)) : [],
      mode: lu.mode === 'DELIVERY' ? 'DELIVERY' : 'PICKUP',
      boutiqueId: BOUTIQUES.some(b => b.id === lu.boutiqueId) ? lu.boutiqueId! : initial.boutiqueId,
      favoris: Array.isArray(lu.favoris) ? lu.favoris : [],
      confort: !!lu.confort,
    };
  } catch {
    return initial;
  }
}

const cleDe = (produitId: string, poidsG: number, options: Record<string, string>, formatIndex?: number, note?: string) =>
  [produitId, poidsG, formatIndex ?? '', Object.entries(options).sort().map(([k, v]) => `${k}=${v}`).join(','), note ?? ''].join('|');

export function FournisseurBoutique({ children }: { children: ReactNode }) {
  const [etat, setEtat] = useState<Persiste>(() => lire());
  const [panierOuvert, setPanierOuvert] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);

  /* Le panier suit le client d'une visite à l'autre. */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(CLE, JSON.stringify(etat));
    } catch {
      /* navigation privée : le panier reste en mémoire, c'est tout. */
    }
  }, [etat]);

  /* Confort de lecture : une échelle appliquée à la racine, en rem. */
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const racine = document.documentElement;
    racine.dataset.confort = etat.confort ? 'true' : 'false';
    racine.dataset.comfort = racine.dataset.confort;
  }, [etat.confort]);

  const annoncer = useCallback<API['annoncer']>((message, action) => {
    setToast({ id: Date.now(), message, libelleAction: action?.libelle, action: action?.faire });
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 5200);
    return () => window.clearTimeout(t);
  }, [toast]);

  const ajouter = useCallback<API['ajouter']>((p, opts = {}) => {
    const formatIndex = p.modele === 'FORMATS' ? (opts.formatIndex ?? 0) : undefined;
    const poidsG = opts.poidsG ?? (formatIndex !== undefined ? p.formats[formatIndex]?.poidsG : undefined) ?? poidsParDefaut(p);
    const quantite = Math.max(1, opts.quantite ?? 1);
    const options = opts.options ?? {};
    const note = opts.note?.trim() ? opts.note.trim().slice(0, 200) : undefined;
    const cle = cleDe(p.id, poidsG, options, formatIndex, note);
    setEtat(e => {
      const existe = e.lignes.find(l => l.cle === cle);
      return {
        ...e,
        lignes: existe
          ? e.lignes.map(l => (l.cle === cle ? { ...l, quantite: Math.min(99, l.quantite + quantite) } : l))
          : [...e.lignes, { cle, produitId: p.id, poidsG, quantite, formatIndex, options, note }],
      };
    });
  }, []);

  const majQuantite = useCallback<API['majQuantite']>((cle, quantite) => {
    setEtat(e => ({
      ...e,
      lignes: quantite <= 0
        ? e.lignes.filter(l => l.cle !== cle)
        : e.lignes.map(l => (l.cle === cle ? { ...l, quantite: Math.min(99, quantite) } : l)),
    }));
  }, []);

  /* Le poids se corrige dans le panier : la ligne change de clé, et fusionne si
     la même combinaison existe déjà. */
  const majPoids = useCallback<API['majPoids']>((cle, poidsG) => {
    setEtat(e => {
      const ligne = e.lignes.find(l => l.cle === cle);
      if (!ligne) return e;
      const neuve = cleDe(ligne.produitId, poidsG, ligne.options, ligne.formatIndex, ligne.note);
      const autres = e.lignes.filter(l => l.cle !== cle);
      const jumelle = autres.find(l => l.cle === neuve);
      return {
        ...e,
        lignes: jumelle
          ? autres.map(l => (l.cle === neuve ? { ...l, quantite: Math.min(99, l.quantite + ligne.quantite) } : l))
          : [...autres, { ...ligne, cle: neuve, poidsG }],
      };
    });
  }, []);

  const retirer = useCallback<API['retirer']>((cle) => {
    setEtat(e => ({ ...e, lignes: e.lignes.filter(l => l.cle !== cle) }));
  }, []);

  const vider = useCallback(() => setEtat(e => ({ ...e, lignes: [] })), []);
  const setMode = useCallback<API['setMode']>((mode) => setEtat(e => ({ ...e, mode })), []);
  const setBoutique = useCallback<API['setBoutique']>((boutiqueId) => setEtat(e => ({ ...e, boutiqueId })), []);
  const basculerFavori = useCallback<API['basculerFavori']>((id) => {
    setEtat(e => ({
      ...e,
      favoris: e.favoris.includes(id) ? e.favoris.filter(x => x !== id) : [...e.favoris, id],
    }));
  }, []);
  const basculerConfort = useCallback(() => setEtat(e => ({ ...e, confort: !e.confort })), []);

  const valeur = useMemo<API>(() => {
    const detaillees: LigneDetaillee[] = etat.lignes.flatMap(l => {
      const p = INDEX.get(l.produitId);
      if (!p) return [];
      const prixUnitaire = prixPour(p, l.poidsG, l.formatIndex);
      return [{ ...l, produit: p, prixUnitaire, total: Math.round(prixUnitaire * l.quantite * 100) / 100 }];
    });
    const sousTotal = Math.round(detaillees.reduce((n, l) => n + l.total, 0) * 100) / 100;
    const frais = fraisLivraison(sousTotal, etat.mode);
    const total = Math.round((sousTotal + frais) * 100) / 100;
    return {
      lignes: detaillees,
      nbArticles: detaillees.reduce((n, l) => n + l.quantite, 0),
      sousTotal,
      frais,
      total,
      mode: etat.mode,
      boutiqueId: etat.boutiqueId,
      panierOuvert,
      favoris: etat.favoris,
      confort: etat.confort,
      toast,
      ajouter,
      majQuantite,
      majPoids,
      retirer,
      vider,
      setMode,
      setBoutique,
      ouvrirPanier: setPanierOuvert,
      basculerFavori,
      basculerConfort,
      annoncer,
      fermerToast: () => setToast(null),
      indisponibles: detaillees.filter(l => !l.produit.disponibleEn.includes(etat.boutiqueId)).map(l => l.produit),
    };
  }, [etat, panierOuvert, toast, ajouter, majQuantite, retirer, vider, setMode, setBoutique, basculerFavori, basculerConfort, annoncer]);

  return <Ctx.Provider value={valeur}>{children}</Ctx.Provider>;
}

export function useBoutique(): API {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useBoutique doit être appelé sous <FournisseurBoutique>');
  return ctx;
}
